// Typ: FindHeightEquilateralTask
// Temat: Trójkąt równoboczny → Wysokość
// Wzór:  h = (a√3) / 2

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// ===== generator =====
export function generateTask() {
  const a = Math.floor(Math.random() * 8) + 3; // bok 3..10
  return {
    type: "FindHeightEquilateralTask",
    data: { a }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt równoboczny o boku
      <InlineMath math={`\\ a=${a}`} />. Oblicz jego wysokość{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        <InlineMath math={'h'} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'h'}
        placeholder="np. (6√3)/2, 3√3, 5.20"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a } = data;
  const expected = (a * Math.sqrt(3)) / 2;

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (obsługa przecinka)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptujemy także zapis dziesiętny z dokładnością do 0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a } = data;
  const g = gcd(a, 2);
  const n = a / g;
  const d = 2 / g;

  let latex;
  if (d === 1) {
    const coeff = n === 1 ? '' : `${n} `;
    latex = `h = ${coeff}\\sqrt{3}`;
  } else {
    latex = `h = \\dfrac{${n} \\sqrt{3}}{${d}}`;
  }

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={latex} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a } = data;

  const g = gcd(a, 2);
  const n = a / g;
  const d = 2 / g;
  const simplifiedLatex =
    d === 1
      ? (n === 1 ? `h = \\sqrt{3}` : `h = ${n} \\sqrt{3}`)
      : `h = \\dfrac{${n} \\sqrt{3}}{${d}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> W trójkącie równobocznym wysokość wyraża się wzorem:</div>
      <BlockMath math={'h = \\dfrac{a\\sqrt{3}}{2}'} />

      <div><strong>Krok 2.</strong> Podstawiamy długość boku:</div>
      <BlockMath math={`h = \\dfrac{${a} \\sqrt{3}}{2}`} />

      {g > 1 && (
        <>
          <div><strong>Krok 3.</strong> Upraszczamy ułamek:</div>
          <BlockMath math={simplifiedLatex} />
        </>
      )}
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "h")} />
    </div>
  );
}
